import React from 'react';
import { notifications } from '../../utils';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { useStyles } from './styles';
import { MedicalSupplyMethod, MedicalSupplyType, MedicalSupplyViewModel } from '../../models/injection';
import { MedicalSupplyMethodSelect, MedicalSupplyTypeSelect } from '../../elements';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';

interface MedicalSupplyAddModalProps {
  opened: boolean;
  onModalAction: (supply: MedicalSupplyViewModel | null) => void;
}

const MedicalSupplyAddModal: React.FC<any> = ({ opened, onModalAction }: MedicalSupplyAddModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [supplyNumber, setSupplyNumber] = React.useState<string>('');
  const [producer, setProducer] = React.useState<string>('');
  const [method, setMethod] = React.useState<MedicalSupplyMethod>(MedicalSupplyMethod.Intermuscular);
  const [registrationNumber, setRegistrationNumber] = React.useState<string>('');
  const [supplyType, setSupplyType] = React.useState<MedicalSupplyType>(MedicalSupplyType.Vaccination);
  const [waitingTime, setWaitingTime] = React.useState<number>(0);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const closeDialog = (text: string | null) => {
    if (text === null) {
      onModalAction(null);
      clearFields();
      return;
    }

    if (!validate()) {
      return;
    }

    onModalAction({
      number: supplyNumber,
      supplyType,
      waitingTime: +waitingTime,
      producer,
      method,
      registrationNumber
    } as MedicalSupplyViewModel);

    clearFields();
  };

  const validate = (): boolean => {
    if (!supplyNumber) {
      notifications.warning(t('f-val-supp-number'));
      return false;
    }

    if (!producer) {
      notifications.warning(t('f-val-supp-producer'));
      return false;
    }

    // withdrawal period is only for Antibiotic type
    if (supplyType === MedicalSupplyType.Antibiotic && waitingTime <= 0) {
      notifications.warning(t('f-val-supp-widthd'));
      return false;
    }

    return true;
  };

  const clearFields = () => {
    setSupplyNumber('');
    setSupplyType(MedicalSupplyType.Vaccination);
    setWaitingTime(0);
    setProducer('');
    setMethod(MedicalSupplyMethod.Intermuscular);
    setRegistrationNumber('');
  };

  const title = t('f-supp-create-title');

  return (
    <Dialog
      id="addSupplyDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
      aria-labelledby="form-dialog-title"
      fullScreen={fullScreen}
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box width="100%" justifyContent="space-between" display="flex">
              <Box display="flex" alignItems="center">
                <IconButton edge="start" color="inherit" onClick={() => closeDialog(null)} aria-label="close">
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">{title}</Typography>
              </Box>

              <IconButton id="print-mob-btn" color="inherit" onClick={() => closeDialog('ok')}>
                <Icon>{'add_circle'}</Icon>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="addSupplyDialogTitle">{title}</DialogTitle>

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item md={6} xs={12}>
            <TextField
              label={t('f-com-number')}
              variant="outlined"
              size="small"
              className={classes.formControl}
              value={supplyNumber}
              onChange={(e) => {
                setSupplyNumber(e.target.value);
              }}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <TextField
              label={t('f-supplies-producer')}
              variant="outlined"
              size="small"
              className={classes.formControl}
              value={producer}
              onChange={(e) => {
                setProducer(e.target.value);
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('f-com-reg-num')}
              variant="outlined"
              size="small"
              className={classes.formControl}
              value={registrationNumber}
              onChange={(e) => {
                setRegistrationNumber(e.target.value);
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MedicalSupplyMethodSelect
              value={method}
              cssClass={classes.formControl}
              onSupplyMethodChange={(value) => {
                setMethod(value);
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MedicalSupplyTypeSelect
              value={supplyType}
              cssClass={classes.formControl}
              onSupplyTypeChange={(value) => {
                setSupplyType(value);
              }}
            />
          </Grid>

          {(supplyType === MedicalSupplyType.Antibiotic || supplyType === MedicalSupplyType.Any) && (
            <Grid item xs={12} md={6}>
              <TextField
                id="waiting-input"
                label={t('f-supp-create-widthd')}
                variant="outlined"
                type="number"
                size="small"
                className={classes.formControl}
                value={waitingTime}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setWaitingTime(newValue);
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {!fullScreen && (
        <>
          <DialogActions>
            <Button
              onClick={() => closeDialog(null)}
              variant="outlined"
              startIcon={<Icon>{'close'}</Icon>}
              color="primary"
            >
              {t('f-com-cancel')}
            </Button>

            <Button
              onClick={() => closeDialog('ok')}
              id="saveBtn"
              startIcon={<Icon>{'add_circle_outline'}</Icon>}
              variant="contained"
              color="primary"
            >
              {t('f-com-create')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default MedicalSupplyAddModal;
